// import components
import { Tooltip, Typography } from '@mui/material';

// import external libraries
import { useEffect, useRef, useState } from 'react';

// import types
import type { OverflowTooltipProps } from './OverflowTooltip.types';

/**
 * A single-line label that ellipsis-truncates when it does not fit, and reveals the full text in a
 * tooltip on hover only when it is actually truncated. An empty tooltip title is what suppresses it
 * while the text still fits — MUI draws no tooltip for an empty title.
 */
const OverflowTooltip = ({
  children,
  ...typographyProps
}: OverflowTooltipProps) => {
  const ref = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const measure = () =>
      setIsOverflowing(element.scrollWidth > element.clientWidth);
    measure();

    // Re-measure when the column resizes, where ResizeObserver exists — jsdom and very old browsers
    // lack it, and the initial measure above is enough to decide the common case.
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children]);

  return (
    <Tooltip title={isOverflowing ? children : ''} arrow>
      <Typography ref={ref} noWrap {...typographyProps}>
        {children}
      </Typography>
    </Tooltip>
  );
};

export default OverflowTooltip;
